/**
 * Custom hook for managing destroy mother state machine
 */

import { useState, useCallback } from 'react';
import type { DestroyMotherState, Plant, BreedingWorkflowResult } from '../types/breeding';
import { destroyPlant } from '../services/breedingApiService';

export function useDestroyMother() {
  const [destroyState, setDestroyState] = useState<DestroyMotherState>({
    status: 'idle',
    decision: null,
    error: '',
    result: null,
  });

  const initializeDestroy = useCallback(() => {
    setDestroyState({
      status: 'prompt',
      decision: null,
      error: '',
      result: null,
    });
  }, []);

  const handleDestroyYes = useCallback(
    async (
      plant: Plant,
      onSuccess?: (updatedRun: Partial<BreedingWorkflowResult>) => void,
      reason: string = 'Self-pollination complete',
      reasonType: number = 1
    ) => {
      if (destroyState.status === 'confirming' || destroyState.status === 'success') {
        return;
      }

      setDestroyState((prev) => ({
        ...prev,
        status: 'confirming',
        error: '',
      }));

      try {
        const result = await destroyPlant(plant.Id, reason, reasonType);

        setDestroyState({
          status: 'success',
          decision: 'yes',
          error: '',
          result,
        });

        // Notify parent of successful destroy with updated data
        if (onSuccess) {
          onSuccess({
            destroyedMother: true,
            destroyReason: reason,
            destroyReasonType: reasonType,
            destroyedAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        setDestroyState({
          status: 'error',
          decision: null,
          error: error instanceof Error ? error.message : 'Failed to destroy mother plant.',
          result: null,
        });
      }
    },
    [destroyState.status]
  );

  const handleDestroyNo = useCallback(
    (onDecline?: (updatedRun: Partial<BreedingWorkflowResult>) => void) => {
      setDestroyState({
        status: 'declined',
        decision: 'no',
        error: '',
        result: null,
      });

      // Notify parent of decline with updated data
      if (onDecline) {
        onDecline({
          destroyedMother: false,
          destroyReason: null,
          destroyReasonType: null,
          destroyedAt: null,
        });
      }
    },
    []
  );

  return {
    destroyState,
    initializeDestroy,
    handleDestroyYes,
    handleDestroyNo,
  };
}
